import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import styles from './DashboardDayAgenda.module.css'
import AgendaRecordings from './AgendaRecordings'
import { ReactComponent as UsersIcon } from '../assets/images/icons/ic-users.svg'

export const lessonDate = value => new Date(/(?:Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`)
export function layoutDayLessons(lessons, day) {
    const from = new Date(`${day}T00:00:00`)
    const until = new Date(from); until.setDate(until.getDate() + 1)
    const minutes = date => date.getHours() * 60 + date.getMinutes()
    const events = lessons.map(lesson => ({ lesson, start: lessonDate(lesson.starts_at), end: lessonDate(lesson.ends_at) }))
        .filter(event => event.start < until && event.end > from && event.end > event.start)
        .sort((a, b) => a.start - b.start)
    let cluster = [], clusterEnd = 0, lanes = []
    const finish = () => cluster.forEach(event => { event.columns = lanes.length })
    events.forEach(event => {
        event.top = event.start < from ? 0 : minutes(event.start)
        event.bottom = event.end >= until ? 1440 : minutes(event.end)
        if (event.top >= clusterEnd) { finish(); cluster = []; lanes = [] }
        let lane = lanes.findIndex(end => end <= event.top)
        if (lane < 0) lane = lanes.length
        lanes[lane] = event.bottom
        event.lane = lane
        cluster.push(event)
        clusterEnd = Math.max(...lanes)
    })
    finish()
    return events
}

export default function DashboardDayAgenda({ lessons, day }) {
    const [now, setNow] = useState(() => new Date())
    const scroll = useRef(null)
    const events = useMemo(() => layoutDayLessons(lessons, day), [lessons, day])
    const isToday = format(now, 'yyyy-MM-dd') === day
    const currentMinute = now.getHours() * 60 + now.getMinutes()
    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 60000)
        return () => clearInterval(timer)
    }, [])
    useEffect(() => {
        const time = new Date()
        const target = format(time, 'yyyy-MM-dd') === day ? time.getHours() * 60 + time.getMinutes() : events[0]?.top ?? 480
        if (scroll.current) scroll.current.scrollTop = Math.max(0, target - 80)
    // Keep the user's scroll position when presence counts refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [day])
    return <>
        {!events.length && <p className={styles.empty}>Nessuna lezione in programma per questo giorno.</p>}
        <div className={styles.scroll} ref={scroll} tabIndex={0} role="region" aria-label="Agenda oraria del giorno">
            <div className={styles.timeline}>
                {Array.from({ length: 24 }, (_, hour) => <div key={hour} className={styles.hour} style={{ top: hour * 60 }}><span>{String(hour).padStart(2, '0')}:00</span></div>)}
                <div className={styles.events}>
                    {events.map(event => {
                        const live = now >= event.start && now < event.end
                        const participants = event.lesson.participants
                        const recording = event.lesson.recordings?.some(item => item.status === 'in-progress')
                        return <article key={event.lesson.id}
                        className={`${styles.event} ${live ? styles.live : ''}`} style={{ top: event.top, height: Math.max(18, event.bottom - event.top - 2), left: `${event.lane / event.columns * 100}%`, width: `calc(${100 / event.columns}% - 4px)` }}
                        title={`${format(event.start, 'HH:mm')}–${format(event.end, 'HH:mm')} · ${event.lesson.name}`}>
                        <div className={styles.eventContent}>
                            {live && <div className={styles.liveInfo}><span className={styles.liveBadge}><i aria-hidden="true" />Live</span><span className={styles.participants}><UsersIcon aria-hidden="true" />{participants == null ? 'Presenze non disponibili' : `${participants} ${participants === 1 ? 'persona' : 'persone'}`}</span></div>}
                            <time>{format(event.start, 'HH:mm')} – {format(event.end, 'HH:mm')}</time><Link className={styles.lessonLink} to={`/lessons/${event.lesson.room_name}`} target="_blank" rel="noopener noreferrer"><strong>{event.lesson.name}</strong></Link><span>{event.lesson.course?.name}</span>
                            {event.lesson.description?.trim() && <div className={styles.description}><Markdown rehypePlugins={[rehypeRaw]}>{event.lesson.description}</Markdown></div>}
                            {live && recording && <span className={styles.recording}>Registrazione in corso</span>}
                            <AgendaRecordings lesson={event.lesson} now={now} />
                        </div>
                    </article>})}
                </div>
                {isToday && <div className={styles.now} style={{ top: currentMinute }} aria-label={`Ora attuale: ${format(now, 'HH:mm')}`}><span>{format(now, 'HH:mm')}</span></div>}
            </div>
        </div>
    </>
}
